import { GETField } from 'src/types/api/field.api'
import { ObjectDataRecord } from 'src/types/api/objects.api'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'
import { Box } from '@mui/material'

import { PayloadRecord } from '@pages/ConfiguredEntity/components/EditCellRender/EditCellRender'

import { ObjectPicker } from '@components/ui/ObjectPicker'

import { useFetchViewColumnByIdQuery } from '@redux/api'

import { FIELD_VALUE_TYPE, HOT_KEY, OBJECT_FIELD_TYPE } from '@constants'
import { AutocompleteOption, EntityType, GETDropDownFilter } from '@types'

type ViewColumnDDE = EntityType & {
  dropDownListFilters?: GETDropDownFilter[] | null
  dropDownWindowFilters?: GETDropDownFilter[] | null
}

type EditCellObjectPickerProps = {
  params: GridRenderCellParams<unknown, ObjectDataRecord, unknown>
  cellObjectFieldModel?: string
  cellField: GETField
  isLoading: boolean
  isSingleEditMode: boolean
  hasWrapRowText: boolean
  value: any
  handleUpdateViewCell?: ({ record }: PayloadRecord) => Promise<void>
  setDropDownWindowOpen: (value: boolean) => void
  onChange: (value: AutocompleteOption | AutocompleteOption[] | string) => void
  invalid: boolean
}

export const EditCellObjectPicker = ({
  params,
  cellObjectFieldModel,
  handleUpdateViewCell,
  isLoading,
  setDropDownWindowOpen,
  value,
  onChange,
  invalid,
  hasWrapRowText,
}: EditCellObjectPickerProps) => {
  const { data: columnDDE, isLoading: isColumnDDELoading } = useFetchViewColumnByIdQuery(
    params.colDef.id,
    {
      skip: !params.colDef.id,
    }
  )

  const isMultiple = params.colDef.fieldType === OBJECT_FIELD_TYPE.ARRAY
  const fixAdornment = isMultiple && !hasWrapRowText

  const handleChange = async newValue => {
    if (
      params.colDef?.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK &&
      !isMultiple &&
      newValue
    ) {
      newValue = { ...newValue, id: newValue.internalId || newValue.id }
    }

    onChange(newValue)
    await handleUpdateViewCell?.(newValue)

    if (!newValue || isMultiple) return

    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  const onChangeMultipleValueDropdownEntity = value => {
    handleChange(value)
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === HOT_KEY.ENTER) {
      params.api.stopCellEditMode({ id: params.id, field: params.field })

      return
    }

    if (e.key === HOT_KEY.ESCAPE) {
      e.stopPropagation()
      params.api.stopCellEditMode({ id: params.id, field: params.field, ignoreModifications: true })

      return
    }
  }

  const handleChangeInput = (value: string) => {
    if (!value) {
      onChange(value)
    }
  }

  if (isLoading || isColumnDDELoading) {
    return <Box sx={{ width: '100%', height: '100%' }} />
  }

  return (
    <ObjectPicker
      {...params}
      autoFocus
      columnId={params.colDef.id}
      disableClearable={false}
      disabled={isLoading}
      dropDownList={columnDDE?.dropDownList}
      dropDownListFilters={(columnDDE as ViewColumnDDE)?.dropDownListFilters}
      dropDownWindow={columnDDE?.dropDownWindow}
      dropDownWindowFilters={(columnDDE as ViewColumnDDE)?.dropDownWindowFilters}
      fixAdornment={fixAdornment}
      formObjectCode={params.colDef.objectCode}
      formObjectId={params.row._id}
      generalFilterField={''}
      handleKeyDown={onKeyDown}
      handleSetDropdownWindowOpenFormState={setDropDownWindowOpen}
      invalid={invalid}
      isDialogWindow={true}
      isDropDownList={Boolean(columnDDE?.dropDownList)}
      isDropDownWindow={Boolean(columnDDE?.dropDownWindow)}
      multiple={isMultiple}
      name={params.field}
      objectCode={cellObjectFieldModel}
      objectValue={params.colDef.objectValue}
      value={value}
      valueType={params.colDef.valueType}
      isOptionEqualToValue={(option, value) =>
        option.id && value.id ? option.id === value.id : false
      }
      sx={{
        ...(fixAdornment && {
          overflow: 'hidden',
          position: 'relative',
          '& .MuiInputBase-root': {
            maxHeight: 23,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              display: 'none !important',
            },
          },
          '& .MuiInputBase-input': {
            mr: '112px !important',
          },
          '& .MuiAutocomplete-endAdornment': {
            backgroundColor: '#fff',
          },
        }),
      }}
      onChange={handleChange}
      onChangeInDropdownEntityMode={handleChange}
      onChangeInput={handleChangeInput}
      onChangeMultipleValueDropdownEntity={onChangeMultipleValueDropdownEntity}
      onValidateInputValue={value => true}
    />
  )
}
